import { useEffect, useMemo, useState } from 'react';
import { addYears, endOfDay, startOfDay } from 'date-fns';
import type { SelfServiceAPI as SelfServiceAPIType } from 'types';
import { SelfServiceAPI } from 'apis';
import { captureErrorInSentry } from 'lib/captureInSentry';
import useDetailsBooking from 'hooks/useDetailsBooking';
import { toCalendarDateFormat } from 'lib/utils';
import getBlockedDates from './utils/getBlockedDates';

const useAvailabilityCalendar = () => {
  const { booking } = useDetailsBooking();
  const [stays, setStays] = useState<SelfServiceAPIType.Stay[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const minDate = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addYears(endOfDay(new Date()), 1), []);
  const startDate = useMemo(() => toCalendarDateFormat(minDate), [minDate]);
  const endDate = useMemo(() => toCalendarDateFormat(maxDate), [maxDate]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);

      try {
        const response = await SelfServiceAPI.getAvailabilityForOffer(
          {
            bookingId: booking.id,
            startDate,
            endDate,
          },
          { signal: controller.signal },
        );

        setStays(response.stays ?? []);
      } catch (err: any) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          captureErrorInSentry(err);
          setStays([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [booking.id, endDate, startDate]);

  const blockedDates = getBlockedDates(stays ?? []);

  return {
    loading,
    minDate,
    maxDate,
    blockedDates,
  };
};

export default useAvailabilityCalendar;
